// src/lib/capture/eventSelection.ts — sticky last-event selection for mobile capture (P3 events).
//
// Persists the event a user last tagged a capture to (conference / sub-event) so each NEW recording
// in the same conference is pre-tagged without re-picking. Mirrors CaptureSheet's last-MODE idiom
// (persistLastMode / getLastCaptureMode): @capacitor/preferences on native (dynamic-imported behind
// isNative() + try/catch so the web bundle never hard-depends on the plugin), localStorage on web.
//
// EVERYTHING fails soft: a persistence error must never break the capture sheet — a failed read just
// means "no sticky event" and a failed write just means the sheet won't pre-tag next time.

import { isNative } from '@/lib/native/platform';

const LAST_EVENT_KEY = 'cbam.capture.lastEvent';

/** The minimal sticky shape — just enough to render the chip and thread the id without a re-fetch. */
export interface StickyEvent {
  eventId: string;
  eventName: string;
}

function isStickyEvent(v: unknown): v is StickyEvent {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as StickyEvent).eventId === 'string' &&
    (v as StickyEvent).eventId.length > 0 &&
    typeof (v as StickyEvent).eventName === 'string'
  );
}

/**
 * Persist (or, when passed null, clear) the sticky event. Native → @capacitor/preferences;
 * web → localStorage. Fails soft.
 */
export async function persistLastEvent(event: StickyEvent | null): Promise<void> {
  const value = event ? JSON.stringify(event) : null;

  if (isNative()) {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      if (value === null) {
        await Preferences.remove({ key: LAST_EVENT_KEY });
      } else {
        await Preferences.set({ key: LAST_EVENT_KEY, value });
      }
      return;
    } catch {
      /* preferences unavailable — fall through to the web store below */
    }
  }

  try {
    if (typeof localStorage !== 'undefined') {
      if (value === null) localStorage.removeItem(LAST_EVENT_KEY);
      else localStorage.setItem(LAST_EVENT_KEY, value);
    }
  } catch {
    /* persistence failed — sheet just won't pre-tag next time */
  }
}

/**
 * Read the sticky event (or null if none / unreadable / malformed). Native → @capacitor/preferences;
 * web → localStorage. Fails soft to null.
 */
export async function getLastEvent(): Promise<StickyEvent | null> {
  let raw: string | null = null;

  if (isNative()) {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: LAST_EVENT_KEY });
      raw = value ?? null;
    } catch {
      /* preferences unavailable — fall through to the web store */
    }
  }

  if (raw === null) {
    try {
      if (typeof localStorage !== 'undefined') raw = localStorage.getItem(LAST_EVENT_KEY);
    } catch {
      raw = null;
    }
  }

  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return isStickyEvent(parsed) ? { eventId: parsed.eventId, eventName: parsed.eventName } : null;
  } catch {
    return null;
  }
}
